/**
 * Workflow Node Validation Utilities
 * Checks if workflow nodes are properly configured and returns validation status
 */

/**
 * Validates an action node and returns validation result
 * @param {object} nodeData - The node's data object
 * @returns {object} { isValid: boolean, errors: string[] }
 */
export const validateActionNode = (nodeData) => {
  const errors = [];
  const actionType = nodeData?.actionType;
  const actionConfig = nodeData?.actionConfig || {};
  const hasValue = (value) => value !== undefined && value !== null && `${value}`.trim() !== '';
  const hasList = (value) => Array.isArray(value) && value.length > 0;

  if (!actionType) {
    errors.push('Action type not selected');
    return { isValid: false, errors };
  }

  switch (actionType) {
    case 'send_email':
    case 'email':
      if (!actionConfig.recipient_type && !actionConfig.recipient_email) {
        errors.push('Email recipient not configured');
      }
      if (actionConfig.recipient_type === 'custom' && !hasValue(actionConfig.custom_email)) {
        errors.push('Custom email not set');
      }
      if (!actionConfig.subject) {
        errors.push('Email subject not set');
      }
      if (!actionConfig.body) {
        errors.push('Email body not set');
      }
      if (actionConfig.send_from === 'personal' && actionConfig.from_email_account === 'specific' && !hasValue(actionConfig.specific_from_email)) {
        errors.push('From email not set');
      }
      break;

    case 'send_notification':
      if (actionConfig.notify_type === 'specific_users' && !hasList(actionConfig.selected_user_ids)) {
        errors.push('Notification recipients not selected');
      }
      if (!actionConfig.message) {
        errors.push('Notification message not set');
      }
      if (!actionConfig.title) {
        errors.push('Notification title not set');
      }
      break;

    case 'create_task':
      if (!actionConfig.task_name && !actionConfig.name) {
        errors.push('Task name not set');
      }
      if (actionConfig.project_source === 'specific' && !hasValue(actionConfig.project_name)) {
        errors.push('Project name not set');
      }
      if (actionConfig.stage_type === 'specific' && !hasValue(actionConfig.stage_name)) {
        errors.push('Stage name not set');
      }
      if (actionConfig.due_date_type === 'specific' && !hasValue(actionConfig.due_date)) {
        errors.push('Due date not set');
      }
      if (actionConfig.due_date_type === 'offset' && !hasValue(actionConfig.due_date_offset)) {
        errors.push('Due date offset not set');
      }
      break;

    case 'update_task':
      // Update task can have various optional fields, at least one should be set
      const hasUpdate = actionConfig.status || actionConfig.priority || 
                        actionConfig.assignees || actionConfig.due_date ||
                        actionConfig.category || actionConfig.stage_id;
      if (!hasUpdate) {
        errors.push('No task update fields configured');
      }
      break;

    case 'assign_task':
      if (!actionConfig.assignee_type && !actionConfig.assignees?.length) {
        errors.push('Assignee not selected');
      }
      break;

    case 'move_stage':
    case 'move_to_stage':
      if (!actionConfig.stage_id && !actionConfig.stage_name) {
        errors.push('Target stage not selected');
      }
      break;

    case 'add_to_workflow':
      if (!actionConfig.target_workflow_id) {
        errors.push('Target workflow not selected');
      }
      break;

    case 'remove_from_workflow':
      if (!actionConfig.target_workflow_id) {
        errors.push('Target workflow not selected');
      }
      break;

    case 'webhook':
      if (!actionConfig.url) {
        errors.push('Webhook URL not set');
      }
      break;

    case 'wait':
      if (!actionConfig.duration && !actionConfig.wait_until) {
        errors.push('Wait duration not configured');
      }
      break;

    case 'update_assignees':
      // Accept new config shape (assignee_source + user_emails) and legacy fields
      if (actionConfig.assignee_action === 'clear') {
        break;
      }
      const hasLegacyAssignee =
        actionConfig.assignee_type ||
        (actionConfig.assignees && actionConfig.assignees.length > 0) ||
        actionConfig.assignee_user_id;
      const hasSpecificUsers =
        typeof actionConfig.user_emails === 'string' &&
        actionConfig.user_emails.split(',').map(e => e.trim()).filter(Boolean).length > 0;
      const hasAssigneeSource = !!actionConfig.assignee_source && actionConfig.assignee_source !== 'specific_users';
      if (!hasLegacyAssignee && !hasSpecificUsers && !hasAssigneeSource) {
        errors.push('Assignee not selected');
      }
      break;

    case 'update_client':
      // At least one field should be updated
      const hasClientUpdate = actionConfig.status || actionConfig.category || 
                              actionConfig.tags || actionConfig.assignee;
      if (!hasClientUpdate) {
        errors.push('No client update fields configured');
      }
      break;

    case 'ai_generate':
      // AI Generate has sensible defaults (content_type='email', audience='client')
      // No required configuration - user can customize but defaults work
      break;

    case 'gpt_action':
      // GPT Action requires a prompt
      if (!actionConfig.prompt) {
        errors.push('GPT prompt not configured');
      }
      break;

    case 'change_category':
      if (!hasValue(actionConfig.category)) {
        errors.push('Category not selected');
      }
      break;

    case 'update_project_manager':
      if ((actionConfig.pm_selection || 'specific') === 'specific' && !hasValue(actionConfig.project_manager_id)) {
        errors.push('Project manager not selected');
      }
      break;

    case 'add_subtask':
      if (!hasValue(actionConfig.subtask_name)) {
        errors.push('Subtask name not set');
      }
      break;

    case 'update_status':
      if (!hasValue(actionConfig.status)) {
        errors.push('Status not set');
      }
      break;

    case 'update_priority':
      if (!hasValue(actionConfig.priority)) {
        errors.push('Priority not set');
      }
      break;

    case 'change_project':
      if (!hasValue(actionConfig.project_name)) {
        errors.push('Project name not set');
      }
      break;

    case 'set_due_date':
      if ((actionConfig.date_type || 'offset') === 'specific' && !hasValue(actionConfig.specific_date)) {
        errors.push('Due date not set');
      }
      if ((actionConfig.date_type || 'offset') === 'offset' && !hasValue(actionConfig.days_offset)) {
        errors.push('Days offset not set');
      }
      break;

    case 'add_comment':
      if (!hasValue(actionConfig.content)) {
        errors.push('Comment not set');
      }
      break;

    case 'add_tag':
    case 'remove_tag':
      if (!hasValue(actionConfig.tags)) {
        errors.push('Tags not set');
      }
      break;

    case 'create_project':
      if (!hasValue(actionConfig.project_name)) {
        errors.push('Project name not set');
      }
      if (actionConfig.project_manager_type === 'specific' && !hasValue(actionConfig.project_manager_id)) {
        errors.push('Project manager not selected');
      }
      break;

    case 'create_client':
      if (!hasValue(actionConfig.client_name)) {
        errors.push('Client name not set');
      }
      break;

    case 'archive_task':
      if (!hasValue(actionConfig.archive_action)) {
        errors.push('Archive action not set');
      }
      break;

    case 'grant_portal_access':
      if (!hasValue(actionConfig.contact_selection)) {
        errors.push('Contact selection not set');
      }
      break;

    case 'send_portal_invite':
      if (!hasValue(actionConfig.contact_source)) {
        errors.push('Contact source not set');
      }
      if (actionConfig.contact_source === 'custom_email' && !hasValue(actionConfig.custom_email)) {
        errors.push('Custom email not set');
      }
      break;

    case 'go_to':
      if (!hasValue(actionConfig.target_node_id)) {
        errors.push('Target node not selected');
      }
      break;

    default:
      // Unknown action types - check if any config exists
      if (Object.keys(actionConfig).length === 0) {
        errors.push('Action not configured');
      }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validates a trigger node
 * @param {object} nodeData - The node's data object
 * @returns {object} { isValid: boolean, errors: string[] }
 */
export const validateTriggerNode = (nodeData) => {
  const errors = [];
  const triggerType = nodeData?.triggerType;
  const triggerConfig = nodeData?.triggerConfig || {};

  if (!triggerType) {
    errors.push('Trigger type not selected');
    return { isValid: false, errors };
  }

  switch (triggerType) {
    case 'webhook':
      // Webhook triggers are usually auto-configured
      break;

    case 'schedule':
      if (!triggerConfig.schedule && !triggerConfig.cron) {
        errors.push('Schedule not configured');
      }
      break;

    case 'task_stage_changed':
    case 'task_status_change':
      // Optional: can filter by specific stages/statuses
      break;

    case 'task_created':
    case 'task_assigned':
    case 'task_updated':
    case 'project_created':
    case 'client_created':
    case 'due_date_approaching':
      // These triggers don't require additional config
      break;

    default:
      // Allow unknown trigger types
      break;
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validates an if/else condition node
 * @param {object} nodeData - The node's data object
 * @returns {object} { isValid: boolean, errors: string[] }
 */
export const validateIfElseNode = (nodeData) => {
  const errors = [];
  const conditions = nodeData?.conditions || [];

  if (conditions.length === 0) {
    errors.push('No conditions configured');
    return { isValid: false, errors };
  }

  // Check each condition has the required fields
  conditions.forEach((condition, index) => {
    if (!condition.field) {
      errors.push(`Condition ${index + 1}: Field not selected`);
    }
    if (!condition.operator) {
      errors.push(`Condition ${index + 1}: Operator not selected`);
    }
    // Value can be empty for some operators like 'is_empty', 'is_not_empty'
    const needsValue = !['is_empty', 'is_not_empty', 'is_true', 'is_false'].includes(condition.operator);
    if (needsValue && !condition.value && condition.value !== 0) {
      errors.push(`Condition ${index + 1}: Value not set`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validates a condition node
 * @param {object} nodeData - The node's data object
 * @returns {object} { isValid: boolean, errors: string[] }
 */
export const validateConditionNode = (nodeData) => {
  const errors = [];
  const conditionType = nodeData?.conditionType;
  const conditionConfig = nodeData?.conditionConfig || {};

  if (!conditionType) {
    errors.push('Condition type not selected');
    return { isValid: false, errors };
  }

  // Most conditions need at least some configuration
  if (Object.keys(conditionConfig).length === 0) {
    errors.push('Condition not configured');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validates any node type
 * @param {object} node - The full node object from ReactFlow
 * @returns {object} { isValid: boolean, errors: string[] }
 */
export const validateNode = (node) => {
  const nodeType = node?.type;
  const nodeData = node?.data || {};

  switch (nodeType) {
    case 'action':
      return validateActionNode(nodeData);
    case 'trigger':
      return validateTriggerNode(nodeData);
    case 'ifelse':
      return validateIfElseNode(nodeData);
    case 'condition':
      return validateConditionNode(nodeData);
    default:
      return { isValid: true, errors: [] };
  }
};

/**
 * Validates all nodes in a workflow
 * @param {array} nodes - Array of ReactFlow nodes
 * @returns {object} { isValid: boolean, nodeErrors: { [nodeId]: { isValid, errors } } }
 */
export const validateWorkflow = (nodes) => {
  const nodeErrors = {};
  let isValid = true;

  nodes.forEach(node => {
    const validation = validateNode(node);
    nodeErrors[node.id] = validation;
    if (!validation.isValid) {
      isValid = false;
    }
  });

  return {
    isValid,
    nodeErrors
  };
};
